import React from "react";
import { PublicDocShell } from "../components/PublicDocShell";
import "./TermsOfServicePage.css";

export const TermsOfServicePage: React.FC = () => {
  return (
    <PublicDocShell
      eyebrow="Legal & Compliance"
      title={
        <>
          Terms of <span className="public-doc-title-gradient">Service</span>
        </>
      }
      subtitle="Last updated: January 28, 2026. These Terms of Service govern your use of Kairos and its services."
    >
      <div className="terms-content">
        {/* Introduction */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            1. Agreement to Terms
          </h2>
          <div className="terms-section-text">
            <p>
              These Terms of Service ("Terms") constitute a legally binding
              agreement between you ("User," "you," or "your") and Cognixis AI
              ("Company," "we," "us," or "our") regarding your use of Kairos,
              our AI-powered job search and career optimization platform,
              including our website, browser extension, mobile applications, and
              related services (collectively, "Services").
            </p>
            <p>
              <strong>Acceptance:</strong> By accessing or using our Services,
              you acknowledge that you have read, understood, and agree to be
              bound by these Terms. If you do not agree to these Terms, you must
              not use our Services.
            </p>
            <p>
              <strong>Eligibility:</strong> You must be at least 18 years old
              and have the legal capacity to enter into binding agreements to
              use our Services. By using our Services, you represent and warrant
              that you meet these requirements.
            </p>
            <p>
              <strong>Modifications:</strong> We reserve the right to modify
              these Terms at any time. We will notify you of material changes by
              posting the updated Terms on our website or through other
              reasonable means. Your continued use of our Services after such
              modifications constitutes acceptance of the updated Terms.
            </p>
          </div>
        </div>

        {/* Services Description */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            2. Description of Services
          </h2>
          <div className="terms-section-text">
            <p>
              Kairos provides AI-powered job search and career optimization
              services, including but not limited to:
            </p>
            <ul className="terms-list">
              <li>
                <strong>Browser Extension:</strong> A browser extension that
                integrates with major job boards (LinkedIn, Indeed, Glassdoor,
                and others) to provide real-time job analysis and application
                assistance
              </li>
              <li>
                <strong>Resume Analysis and Optimization:</strong> AI-powered
                resume analysis that identifies errors, discrepancies, and
                improvement opportunities, with professional rewriting
                suggestions
              </li>
              <li>
                <strong>ATS Processing:</strong> Automated Applicant Tracking
                System (ATS) processing to extract and analyze resume data for
                compatibility with job requirements
              </li>
              <li>
                <strong>Resume Matching:</strong> Intelligent matching of your
                resume to job descriptions with compatibility scores and
                improvement recommendations
              </li>
              <li>
                <strong>Job Application Tracking:</strong> Comprehensive
                application lifecycle management with real-time tracking,
                contact management, and status updates
              </li>
              <li>
                <strong>Interview Preparation:</strong> AI-generated interview
                preparation materials including company-specific insights,
                common questions, and personalized recommendations
              </li>
              <li>
                <strong>Company Intelligence:</strong> AI-powered company
                research including recent news, layoffs, hirings, management
                changes, employee reviews, and customer feedback
              </li>
              <li>
                <strong>Intelligent Job Search:</strong> AI-enhanced job search
                across multiple platforms with intelligent filtering and
                recommendations
              </li>
              <li>
                <strong>Service Plans:</strong> Subscription-based service plans
                with varying feature access and usage limits
              </li>
            </ul>
            <p>
              <strong>Service Availability:</strong> We strive to maintain high
              service availability but do not guarantee uninterrupted access. We
              may temporarily suspend Services for maintenance, updates, or
              other operational reasons.
            </p>
            <p>
              <strong>Beta Features:</strong> Some Services may be offered in
              beta or experimental form. These features are provided "as is" and
              may be unstable or incomplete.
            </p>
            <p>
              <strong>Service Plan Limits:</strong> Your access to certain
              features and usage limits are determined by your active service
              plan. Feature availability and limits may change based on your
              subscription tier.
            </p>
          </div>
        </div>

        {/* User Accounts */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            3. User Accounts and Registration
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">3.1 Account Creation</h3>
            <p>
              To access certain Services, you may be required to create an
              account. You agree to:
            </p>
            <ul className="terms-list">
              <li>
                Provide accurate, current, and complete information during
                registration
              </li>
              <li>
                Maintain and promptly update your account information, including
                resume data and profile details
              </li>
              <li>
                Maintain the security and confidentiality of your account
                credentials
              </li>
              <li>
                Accept responsibility for all activities that occur under your
                account
              </li>
            </ul>
            <p>
              <strong>Stripe Payment Account:</strong> When you register for an
              account with Kairos, a corresponding account will be automatically
              created in the Stripe payment processing system to facilitate
              subscription management and payment processing. This Stripe
              account is linked to your Kairos account and is used exclusively
              for processing subscription payments and managing billing. By
              registering, you acknowledge and consent to the creation of this
              Stripe account and agree to Stripe's Terms of Service and Privacy
              Policy, which govern your relationship with Stripe. You may access
              your Stripe account information through your Kairos account
              settings or directly through Stripe's customer portal.
            </p>

            <h3 className="terms-subsection-title">3.2 Account Security</h3>
            <p>You are responsible for:</p>
            <ul className="terms-list">
              <li>Creating strong, unique passwords</li>
              <li>Not sharing your account credentials with others</li>
              <li>
                Immediately notifying us of any unauthorized use of your account
              </li>
              <li>Logging out of your account when using shared devices</li>
              <li>
                Securing your browser extension installation and preventing
                unauthorized access
              </li>
            </ul>

            <h3 className="terms-subsection-title">3.3 Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate your account at any
              time for violations of these Terms or for any other reason at our
              sole discretion. Upon termination, your access to Services,
              including stored resumes, job applications, and analysis data, may
              be permanently deleted.
            </p>
          </div>
        </div>

        {/* Acceptable Use */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            4. Acceptable Use Policy
          </h2>
          <div className="terms-section-text">
            <p>
              You agree to use our Services only for lawful purposes and in
              accordance with these Terms. You agree not to:
            </p>
            <ul className="terms-list">
              <li>
                <strong>Illegal Activities:</strong> Use Services for any
                unlawful purpose or to solicit others to perform unlawful acts
              </li>
              <li>
                <strong>Intellectual Property Violations:</strong> Infringe upon
                or violate our intellectual property rights or the intellectual
                property rights of others, including submitting resumes or
                content that violates copyright or trademark laws
              </li>
              <li>
                <strong>Harmful Content:</strong> Upload, transmit, or
                distribute harmful, offensive, defamatory, or inappropriate
                content through our Services
              </li>
              <li>
                <strong>System Abuse:</strong> Attempt to gain unauthorized
                access to our systems, interfere with Service operation, or
                attempt to bypass service plan limits
              </li>
              <li>
                <strong>Data Mining:</strong> Use automated systems to extract
                data from our Services without permission, including scraping
                job listings or company intelligence data
              </li>
              <li>
                <strong>Spam and Harassment:</strong> Send unsolicited
                communications or engage in harassment through our Services
              </li>
              <li>
                <strong>Resume Misrepresentation:</strong> Submit false,
                misleading, or fraudulent resume information or job application
                data
              </li>
              <li>
                <strong>Extension Misuse:</strong> Use the browser extension to
                interfere with job board functionality, extract unauthorized
                data, or violate job board terms of service
              </li>
              <li>
                <strong>Service Plan Violations:</strong> Attempt to circumvent
                service plan limits, share accounts to exceed individual plan
                limits, or use multiple accounts to avoid subscription fees
              </li>
            </ul>
            <p>
              <strong>Violation Consequences:</strong> Violation of this policy
              may result in immediate account suspension or termination, and we
              may report violations to appropriate authorities or affected job
              boards.
            </p>
          </div>
        </div>

        {/* Intellectual Property */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"
              />
            </svg>
            5. Intellectual Property Rights
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">5.1 Company Ownership</h3>
            <p>
              All content, features, and functionality of our Services,
              including but not limited to text, graphics, logos, software, AI
              models, resume analysis algorithms, and browser extension code,
              are owned by Cognixis AI or its licensors and are protected by
              copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="terms-subsection-title">5.2 User Content</h3>
            <p>
              You retain ownership of content you submit to our Services,
              including resumes, job applications, and profile information.
              However, by submitting content, you grant us a worldwide,
              non-exclusive, royalty-free license to use, reproduce, modify, and
              distribute your content in connection with providing our Services,
              including:
            </p>
            <ul className="terms-list">
              <li>
                Processing and analyzing your resume for ATS compatibility and
                job matching
              </li>
              <li>
                Generating interview preparation materials and company
                intelligence based on your job applications
              </li>
              <li>Storing and managing your job application data</li>
              <li>
                Improving our AI models and Services through anonymized and
                aggregated data analysis
              </li>
            </ul>

            <h3 className="terms-subsection-title">5.3 AI-Generated Content</h3>
            <p>
              Content generated using our AI systems, including resume
              improvements, interview preparation materials, and company
              intelligence, is provided for your use but remains subject to
              these Terms. You are responsible for reviewing and verifying all
              AI-generated content before use. We do not guarantee the accuracy,
              completeness, or suitability of AI-generated content for your
              specific needs.
            </p>

            <h3 className="terms-subsection-title">5.4 License Restrictions</h3>
            <p>You may not:</p>
            <ul className="terms-list">
              <li>
                Copy, modify, or distribute our proprietary content, including
                browser extension code, without permission
              </li>
              <li>
                Reverse engineer or attempt to extract source code from our
                Services or browser extension
              </li>
              <li>
                Use our trademarks or trade names (including "Kairos" and
                "Cognixis AI") without written consent
              </li>
              <li>
                Remove or alter copyright notices or proprietary markings from
                our Services
              </li>
              <li>
                Redistribute or resell our Services, browser extension, or
                AI-generated content
              </li>
            </ul>
          </div>
        </div>

        {/* Privacy and Data */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            6. Privacy and Data Protection
          </h2>
          <div className="terms-section-text">
            <p>
              Your privacy is important to us. Our collection, use, and
              protection of your personal information is governed by our Privacy
              Policy, which is incorporated into these Terms by reference.
            </p>
            <p>
              <strong>Data Processing:</strong> By using our Services, you
              consent to our collection and processing of your data as described
              in our Privacy Policy, including:
            </p>
            <ul className="terms-list">
              <li>
                Resume data and job application information for analysis and
                matching
              </li>
              <li>Browser extension usage data for service improvement</li>
              <li>Job board interaction data for application tracking</li>
              <li>
                Service plan and subscription information for account management
              </li>
            </ul>
            <p>
              <strong>AI Training:</strong> We may use anonymized and aggregated
              data to improve our AI systems, including resume analysis
              algorithms and job matching models. This data will not contain
              personally identifiable information.
            </p>
            <p>
              <strong>Data Retention:</strong> We retain your data only as long
              as necessary to provide our Services and comply with legal
              obligations. You may request deletion of your data at any time
              through your account settings or by contacting us.
            </p>
            <p>
              <strong>Resume and Application Data:</strong> Your resume data,
              job applications, and related information are stored securely. We
              do not share your resume or application data with employers or
              third parties without your explicit consent, except as necessary
              to provide our Services.
            </p>
            <p>
              <strong>Data We Store and Sharing:</strong> Any data we store is
              only what is necessary to provide the Services and process your
              requests. We do not sell your personal information and we do not
              share your data with anyone for marketing or other purposes. We
              only share data with the third-party service providers listed
              below as necessary to operate the Services (for example, sending
              your resume text to our AI provider to generate analysis).
            </p>

            <h3 className="terms-subsection-title">
              6.5 Third-Party Services Used by Kairos
            </h3>
            <p>
              Kairos uses the following third-party services to provide the
              platform. Each has its own privacy policy governing how they
              handle data. By using our Services, you acknowledge that your
              information may be processed by these providers as described
              below.
            </p>
            <ul className="terms-list">
              <li>
                <strong>OpenAI</strong> — We use OpenAI’s AI models to power
                resume analysis, job matching, interview preparation, company
                intelligence summaries, tailored resume suggestions, ATS
                processing, and other AI-generated content. Your resume text,
                job descriptions, and related content you submit are sent to
                OpenAI only to generate responses for your use. OpenAI’s privacy
                policy:{" "}
                <a
                  href="https://openai.com/policies/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://openai.com/policies/privacy-policy
                </a>
                .
              </li>
              <li>
                <strong>Tavily</strong> — We use Tavily for web search when you
                use Company Intelligence (e.g., company news, layoffs, reviews)
                and Intelligent Job Search. Search queries and results are
                processed by Tavily to return relevant, up-to-date information.
                Tavily’s privacy policy:{" "}
                <a
                  href="https://tavily.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://tavily.com/privacy
                </a>
                .
              </li>
              <li>
                <strong>Stripe</strong> — We use Stripe to process subscription
                payments and manage billing. Payment and billing information you
                provide is handled by Stripe in accordance with their policies.
                Stripe’s privacy policy:{" "}
                <a
                  href="https://stripe.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://stripe.com/privacy
                </a>
                .
              </li>
              <li>
                <strong>Amazon Cognito</strong> — We use Amazon Cognito for user
                authentication (sign-up, sign-in, password reset). Your email,
                password (hashed), and account identifiers are processed by
                Cognito to secure your account. Amazon’s privacy notice:{" "}
                <a
                  href="https://aws.amazon.com/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://aws.amazon.com/privacy/
                </a>
                .
              </li>
              <li>
                <strong>Amazon Web Services (AWS)</strong> — Our application and
                data are hosted on AWS. Your profile data, job applications,
                resumes, and other stored information are kept on AWS
                infrastructure in accordance with our security and privacy
                practices. Amazon’s privacy notice:{" "}
                <a
                  href="https://aws.amazon.com/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://aws.amazon.com/privacy/
                </a>
                .
              </li>
            </ul>
            <p>
              We do not control these providers’ privacy practices. Their
              policies linked above describe how they collect, use, and protect
              data. We use them only as needed to deliver the Services to you
              and do not share your data with any other parties.
            </p>
          </div>
        </div>

        {/* Service Plans and Subscriptions */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            7. Service Plans and Subscriptions
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">7.1 Service Plans</h3>
            <p>
              Kairos offers various service plans with different feature access
              and usage limits. Your access to features, including resume
              analysis, job matching, interview preparation, and company
              intelligence, is determined by your active service plan tier.
            </p>
            <p>
              <strong>Plan Features:</strong> Each service plan includes
              specific features and usage limits. Features and limits are
              subject to change, and we will notify you of material changes to
              your plan.
            </p>
            <p>
              <strong>Usage Limits:</strong> Service plans may include limits on
              the number of resume analyses, job applications, interview
              preparation requests, or company intelligence searches. Exceeding
              plan limits may require upgrading to a higher tier or purchasing
              additional usage.
            </p>

            <h3 className="terms-subsection-title">7.2 Subscription Terms</h3>
            <p>If you subscribe to a paid service plan:</p>
            <ul className="terms-list">
              <li>
                Subscription fees are charged in advance on a monthly or annual
                basis
              </li>
              <li>
                Your subscription automatically renews unless cancelled before
                the renewal date
              </li>
              <li>
                You are responsible for all charges incurred under your account
              </li>
              <li>
                We reserve the right to change subscription fees with 30 days'
                notice
              </li>
            </ul>

            <h3 className="terms-subsection-title">
              7.3 Cancellation and Refunds
            </h3>
            <p>
              You may cancel your subscription at any time through your account
              settings. Cancellation takes effect at the end of your current
              billing period. We do not provide refunds for partial billing
              periods, except as required by law or at our sole discretion.
            </p>
            <p>
              <strong>Effect of Cancellation:</strong> Upon cancellation, you
              will retain access to paid features until the end of your current
              billing period. After cancellation, your account will revert to
              the free tier (if available) or be terminated.
            </p>
          </div>
        </div>

        {/* Disclaimers and Limitations */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            8. Disclaimers and Limitations of Liability
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">8.1 Service Disclaimers</h3>
            <p>
              <strong>AS-IS BASIS:</strong> Our Services are provided "as is"
              and "as available" without warranties of any kind, either express
              or implied.
            </p>
            <p>
              <strong>AI Limitations:</strong> While we strive for accuracy,
              AI-generated content, including resume analysis, job matching
              scores, interview preparation materials, and company intelligence,
              may contain errors or inaccuracies. We do not guarantee the
              accuracy, completeness, or reliability of AI-generated content or
              recommendations.
            </p>
            <p>
              <strong>AI Technology and Data Collection:</strong> Our Services
              utilize artificial intelligence and machine learning technologies
              provided by third-party AI service providers. When you use our
              AI-powered features, such as resume analysis, job matching,
              interview preparation, or company intelligence, your data
              (including resumes, job descriptions, and application information)
              may be processed by these third-party AI systems. Cognixis AI is
              not responsible for, and disclaims all liability related to, any
              information collected, processed, stored, or used by third-party
              AI service providers. This includes, but is not limited to, data
              collection practices, data retention policies, data security
              measures, and any use of your information by AI service providers
              for training, improvement, or other purposes. You acknowledge that
              AI systems may collect and process your information in ways that
              are beyond our direct control, and you use our AI-powered features
              at your own risk.
            </p>
            <p>
              <strong>Third-Party AI Services:</strong> By using our AI-powered
              features, you agree that your information may be shared with
              third-party AI service providers as necessary to provide the
              Services. We are not responsible for the privacy practices, data
              handling, or security measures of these third-party AI providers.
              The specific AI service providers we utilize may change from time
              to time, and we are not obligated to disclose the identity of
              these providers.
            </p>
            <p>
              <strong>Privacy Protection Measures:</strong> Cognixis AI
              implements comprehensive privacy protection measures and utilizes
              available configuration settings designed to prevent the use of
              customer data for AI model training, data sharing, or other
              purposes beyond the direct provision of our Services. We employ
              industry-standard privacy controls, data processing agreements,
              and technical safeguards to protect your information. However, we
              cannot guarantee or warrant that third-party AI service providers
              will fully comply with these settings or restrictions, as their
              data handling practices are beyond our direct control. While we
              take all reasonable steps to protect your privacy, you acknowledge
              that absolute control over third-party AI provider data practices
              cannot be guaranteed.
            </p>
            <p>
              <strong>Job Application Outcomes:</strong> We do not guarantee
              that using our Services will result in job offers, interviews, or
              successful job applications. Job search outcomes depend on
              numerous factors beyond our control.
            </p>
            <p>
              <strong>Third-Party Services:</strong> Our Services integrate with
              third-party job boards and services. We are not responsible for
              the availability, accuracy, or content of third-party services,
              including job listings, company information, or application
              processes.
            </p>
            <p>
              <strong>Browser Extension:</strong> The browser extension is
              provided "as is" and may not function correctly on all job boards
              or browser versions. We are not responsible for any issues arising
              from extension compatibility or job board changes.
            </p>

            <h3 className="terms-subsection-title">
              8.2 Limitation of Liability
            </h3>
            <p>
              <strong>Exclusion of Damages:</strong> To the maximum extent
              permitted by law, Cognixis AI shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages,
              including but not limited to lost job opportunities, lost wages,
              or damages resulting from reliance on AI-generated content.
            </p>
            <p>
              <strong>Liability Cap:</strong> Our total liability to you for any
              claims arising from or related to these Terms or our Services
              shall not exceed the amount you paid us in the twelve months
              preceding the claim, or $100, whichever is greater.
            </p>
            <p>
              <strong>Essential Purpose:</strong> The limitations in this
              section are fundamental elements of the basis of the bargain
              between you and us.
            </p>
          </div>
        </div>

        {/* Indemnification */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            9. Indemnification
          </h2>
          <div className="terms-section-text">
            <p>
              You agree to defend, indemnify, and hold harmless Cognixis AI and
              its officers, directors, employees, agents, and affiliates from
              and against any claims, damages, obligations, losses, liabilities,
              costs, or debt arising from:
            </p>
            <ul className="terms-list">
              <li>Your use of our Services, including the browser extension</li>
              <li>Your violation of these Terms</li>
              <li>
                Your violation of any third-party rights, including job board
                terms of service
              </li>
              <li>Your violation of applicable laws or regulations</li>
              <li>
                Content you submit or generate using our Services, including
                false or misleading resume information
              </li>
              <li>
                Your use of AI-generated content in job applications or other
                contexts
              </li>
            </ul>
            <p>
              <strong>Defense Cooperation:</strong> We reserve the right to
              assume the exclusive defense and control of any matter subject to
              indemnification by you, and you agree to cooperate with our
              defense of such claims.
            </p>
          </div>
        </div>

        {/* Termination */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            10. Termination
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">10.1 Termination by You</h3>
            <p>
              You may terminate your account and stop using our Services at any
              time by contacting us or using the account deletion features in
              our Services. You are responsible for uninstalling the browser
              extension if you no longer wish to use it.
            </p>

            <h3 className="terms-subsection-title">10.2 Termination by Us</h3>
            <p>
              We may terminate or suspend your access to our Services
              immediately, without prior notice, for any reason, including but
              not limited to:
            </p>
            <ul className="terms-list">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Non-payment of subscription fees (if applicable)</li>
              <li>Extended periods of inactivity</li>
              <li>Abuse of service plan limits or features</li>
            </ul>

            <h3 className="terms-subsection-title">
              10.3 Effect of Termination
            </h3>
            <p>Upon termination:</p>
            <ul className="terms-list">
              <li>
                Your right to use our Services, including the browser extension,
                ceases immediately
              </li>
              <li>
                We may delete your account and associated data, including
                resumes, job applications, and analysis results
              </li>
              <li>
                Any active subscriptions will be cancelled, and no refunds will
                be provided for partial billing periods
              </li>
              <li>
                Provisions of these Terms that survive termination remain in
                effect
              </li>
            </ul>
          </div>
        </div>

        {/* Governing Law */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l1-6m-1 6l-3-1m3 1l3 9a5.002 5.002 0 01-6.001 0M18 7l-3 9m3-9l-6-2m0-2v2m0 16V5m0 16H9m3-16h3"
              />
            </svg>
            11. Governing Law and Dispute Resolution
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">11.1 Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the
              laws of the State of Utah, United States, without regard to
              conflict of law principles.
            </p>

            <h3 className="terms-subsection-title">11.2 Dispute Resolution</h3>
            <p>
              <strong>Mandatory Arbitration:</strong> Any dispute arising from
              or relating to these Terms or our Services shall be resolved
              through binding arbitration in accordance with the rules of the
              American Arbitration Association.
            </p>
            <p>
              <strong>Class Action Waiver:</strong> You agree to resolve
              disputes individually and waive any right to participate in class
              action lawsuits or class-wide arbitration.
            </p>
            <p>
              <strong>Small Claims Exception:</strong> Claims for amounts less
              than $10,000 may be brought in small claims court in Salt Lake
              County, Utah.
            </p>
          </div>
        </div>

        {/* Miscellaneous */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            12. Miscellaneous Provisions
          </h2>
          <div className="terms-section-text">
            <h3 className="terms-subsection-title">12.1 Severability</h3>
            <p>
              If any provision of these Terms is found to be unenforceable or
              invalid, the remaining provisions will continue in full force and
              effect.
            </p>

            <h3 className="terms-subsection-title">12.2 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy and any other
              agreements referenced herein, constitute the entire agreement
              between you and Cognixis AI regarding our Services.
            </p>

            <h3 className="terms-subsection-title">12.3 Waiver</h3>
            <p>
              Our failure to enforce any provision of these Terms does not
              constitute a waiver of that provision or any other provision.
            </p>

            <h3 className="terms-subsection-title">12.4 Assignment</h3>
            <p>
              You may not assign or transfer your rights or obligations under
              these Terms without our written consent. We may assign these Terms
              without restriction.
            </p>

            <h3 className="terms-subsection-title">12.5 Force Majeure</h3>
            <p>
              We shall not be liable for any failure to perform due to
              circumstances beyond our reasonable control, including but not
              limited to natural disasters, government actions, technical
              failures, or job board service disruptions.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="terms-section">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            13. Contact Information
          </h2>
          <div className="terms-section-text">
            <p>
              If you have any questions about these Terms of Service, please
              contact us:
            </p>
            <div className="terms-contact-box">
              <p>
                <strong>Email:</strong> legal@cognixisai.com
              </p>
              <p>
                <strong>Phone:</strong> (801) 306-5060
              </p>
              <p>
                <strong>Support Email:</strong> support@cognixisai.com
              </p>
            </div>
            <p>
              <strong>Legal Department:</strong> For legal matters, please
              contact our legal team at legal@cognixisai.com.
            </p>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="terms-section terms-section-warning">
          <h2 className="terms-section-title">
            <svg
              className="terms-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            Legal Disclaimer
          </h2>
          <div className="terms-section-text">
            <p>
              <strong>Important Legal Notice:</strong> These Terms of Service
              constitute a legally binding contract between you and Cognixis AI.
              By using our Services, you acknowledge that you have read,
              understood, and agreed to be bound by all terms and conditions
              contained herein.
            </p>
            <p>
              <strong>Legal Advice:</strong> These Terms are not intended to
              constitute legal advice. If you have questions about your legal
              rights or obligations, please consult with a qualified attorney.
            </p>
            <p>
              <strong>Enforcement:</strong> We reserve the right to enforce
              these Terms to the fullest extent permitted by law, including
              seeking injunctive relief and monetary damages for violations.
            </p>
            <p>
              <strong>Updates:</strong> These Terms may be updated periodically.
              Continued use of our Services after updates constitutes acceptance
              of the modified Terms.
            </p>
          </div>
        </div>
      </div>
    </PublicDocShell>
  );
};
